import classNames from "classnames";
import { useEffect, useMemo, useState, type FC } from "react";
import { Accordion, AccordionBody, AccordionHeader, AccordionItem, CardBody } from "react-bootstrap";
import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import { type UseFormReturn } from "react-hook-form";
import { z } from "zod";

import moment from "moment";
import { useAccessControl } from "../../app/use-access-control";
import { useCenterConstants, type ServiceDictionary, type SubServiceDictionary } from "../../hooks/use-center";
import { familyContactSchema, useFamilyConstants, type FamilyContact } from "../../hooks/use-families";
import { useInquiryConstants, type InquiryFormData } from "../../hooks/use-inquiries";
import { CheckboxField, CheckboxFieldset } from "../forms/checkbox-field";
import { isValidRequiredDateString } from "../forms/form-helpers";
import { InputField } from "../forms/input-field";
import { MultiSelectField, MultiSelectOption } from "../forms/multi-select-field";
import { SelectField } from "../forms/select-field";
import { InitialContact } from "../inquiry-list/tab-content/inquiry-dashboard/initial-contact";
import { ContactSearchController } from "../search-combobox/contact-search";

export const discountTypeCodes = new Map([
  ["Dollar", "$"],
  ["Percent", "%"],
  ["$", "Dollar"],
  ["%", "Percent"]
]);

const dollarsSchema = z.coerce
  .string()
  // Input type="number" allows for '+', '-', 'e'.  e.g. +1e-2 = 0.01. When it has those characters
  // and it cannot be represented as a number it's value = "". So we need to check to make sure that
  // the value is not "". the following !isNaN(Number(value)) check is NOT necessary as the input
  // already checks this but left there for some sanity
  .refine(value => value && !isNaN(Number(value)))
  .transform(value => Number(value))
  .pipe(
    z
      .number()
      .min(0, { message: "Value must be positive" })
      .lte(99999.99, { message: "Value must be less than or equal to 99999.99" })
      .step(0.01, { message: "Value must be in increments of 0.01" })
  );

// split the services validation into its own z object and do an interseciton w/ inquirySchema
// if we put it in the same object as the inquirySchema the validations for in superRefine for
// subject/test do not happen until all the validations in the entire inquirySchema object pass
const servicesSchema = z
  .object({
    serviceId: z.string().min(1, { message: "service is required" }),
    selectedSubService: z.string().min(1, { message: "subservice is required" }),
    selectedSubjectOrTest: z.string().default(""),
    multipleSubjectIds: z.array(z.string()).default([])
  })
  .superRefine(({ selectedSubService, selectedSubjectOrTest, multipleSubjectIds }, ctx) => {
    if (selectedSubService) {
      const [, totalLength] = selectedSubService.split("::");

      if (Number(totalLength) > 0 && !selectedSubjectOrTest && !multipleSubjectIds.length) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Subject or test is required",
          path: ["selectedSubjectOrTest"]
        });
      }
    }
  });

let DISCOUNT_REASON_OTHER_ID = "";
const aeFeeSchema = z
  .object({
    baseFee: dollarsSchema,
    discountValue: dollarsSchema,
    discountReasonId: z.string(),
    discountReasonOther: z.string().optional(),
    discountType: z.enum(["$", "%"])
  })
  .superRefine(({ baseFee, discountType, discountValue, discountReasonId, discountReasonOther }, ctx) => {
    if (discountType && discountValue && !discountReasonId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Discount reason is required",
        path: ["discountReasonId"]
      });
    }
    if (discountReasonId === DISCOUNT_REASON_OTHER_ID && !discountReasonOther?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Other Discount Reason Details is required",
        path: ["discountReasonOther"]
      });
    }
    if (discountType === "%" && discountValue > 100) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Discount percentage cannot be more than 100",
        path: ["discountValue"]
      });
    }
    if (discountType === "$" && discountValue > baseFee) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Discount amount must be less than Center AE Fee",
        path: ["discountValue"]
      });
    }
  })
  .optional();

let DETAILS_TAB = true;

export const inquirySchema = z
  .object({
    id: z.string().optional(),
    inquirer: familyContactSchema,
    student: familyContactSchema,
    family: z.object({ id: z.string() }).passthrough().nullable(),
    relationshipId: z.string().min(1, { message: "Relationship is required" }),
    aeFee: aeFeeSchema,
    standardHourlyTuitionQuoted: dollarsSchema,
    privateHourlyTuitionQuoted: dollarsSchema,
    callbackSpecialQuoted: dollarsSchema,
    regFee: dollarsSchema,
    takenBy: z.object({ id: z.string() }), //dont need to pass through
    sourceId: z.string().min(1, { message: "Contact Method is required" }),
    referredById: z.string().min(1, { message: "Referred By is required" }),
    inquiryDate: z.string().refine(
      value => {
        return (
          DETAILS_TAB ||
          isValidRequiredDateString(
            value,
            moment().format("YYYY-MM-DD"),
            moment().subtract(180, "days").format("YYYY-MM-DD")
          )
        );
      },
      { message: "Inquiry Date must be within 180 days" }
    ),
    inquiryTimeReceived: z.string(),
    durationMinutes: z.coerce
      .number()
      .min(0, { message: "Must be a postive number" })
      .max(2147483647, { message: "Must be less than or equal to 2147483647" })
      .optional()
  })
  .passthrough()
  .and(servicesSchema)
  .transform(input => {
    const { selectedSubService, selectedSubjectOrTest } = input;
    const [subServiceId] = selectedSubService.split("::");
    const [subjectOrTestId, subjectOrTest] = selectedSubjectOrTest.split("::");
    const inquiryTimestamp = moment(`${input.inquiryDate}T${input.inquiryTimeReceived}Z`);

    return {
      ...input,
      inquirerId: input.inquirer.id,
      studentId: input.student.id,
      familyId: input.family?.id === undefined ? undefined : input.family?.id ? input.family.id : null,
      inquiryDate: inquiryTimestamp.utc().format(),
      inquiryTimeReceived: inquiryTimestamp.utc().format(),
      serviceId: input.serviceId || undefined,
      programId: subServiceId || undefined,
      testId: subjectOrTest === "testId" ? subjectOrTestId : null,
      subjectIds: input.multipleSubjectIds?.length
        ? input.multipleSubjectIds
        : subjectOrTest === "subjectId"
          ? [subjectOrTestId]
          : null,
      aeFee: {
        ...input.aeFee,
        discountReasonId: input.aeFee?.discountReasonId || undefined,
        discountReasonOther: input.aeFee?.discountReasonOther || undefined
      },
      takenById: input.takenBy.id
    };
  });

export interface InquiryFormLeftPaneProps {
  onSelectInquirer: (contact: FamilyContact) => void;
  onSelectStudent: (contact: FamilyContact) => void;
  handleShowInquirer: () => void;
  handleShowStudent: () => void;
  showContactFamilies: () => void;
  inquiryForm: UseFormReturn<InquiryFormData>;
  onCreateNewContact: (type: "inquirer" | "student") => void;
  isDetailsTab?: boolean;
  detailsTabHeader?: JSX.Element;
  readOnly?: boolean;
}

const servicesThatDisableRegistrationFee = [
  "Premier ACT",
  "ACT In-School Exam Prep",
  "ACT Group Testing",
  "Premier SAT",
  "SAT Direct Enroll - 14",
  "SAT Direct Enroll - 32",
  "SAT In-School Exam Prep",
  "SAT Group Testing",
  "Standard E1",
  "Learning Center",
  "Learning Center 1 to 1",
  "Learning Center 4 to 1",
  "Standard Subject Tutoring"
];
const servicesThatDisableStandardHourlyFee = [
  "Premier ACT",
  "ACT In-School Exam Prep",
  "ACT Group Testing",
  "Premier SAT",
  "SAT In-School Exam Prep",
  "SAT Group Testing",
  "Standard Subject Tutoring"
];
const servicesThatDisablePrivHourlyFee = [
  "ACT Direct Enroll - 14",
  "ACT Direct Enroll - 32",
  "SAT Direct Enroll - 14",
  "SAT Direct Enroll - 32",
  "Standard E1"
];
const servicesThatDisableAEFee = [
  "ACT Direct Enroll - 14",
  "ACT Direct Enroll - 32",
  "SAT Direct Enroll - 14",
  "SAT Direct Enroll - 32"
];
const servicesThatEnableAEFee = [
  "Premier ACT",
  "ACT In-School Exam Prep",
  "ACT Group Testing",
  "Premier SAT",
  "SAT In-School Exam Prep",
  "SAT Group Testing",
  "Standard E1",
  "Learning Center",
  "Learning Center 1 to 1",
  "Learning Center 4 to 1",
  "Standard Subject Tutoring"
];
const servicesThatEnablePrivHourlyFee = [
  "Premier ACT",
  "ACT In-School Exam Prep",
  "ACT Group Testing",
  "Premier SAT",
  "SAT In-School Exam Prep",
  "SAT Group Testing",
  "Learning Center",
  "Learning Center 1 to 1",
  "Learning Center 4 to 1",
  "Standard Subject Tutoring"
];
const sericesThatEnableRegistrationFee = [
  "ACT Direct Enroll - 14",
  "ACT Direct Enroll - 32",
  "SAT Direct Enroll - 14",
  "SAT Direct Enroll - 32"
];
const servicesThatEnableStandardHourlyFee = [
  "ACT Direct Enroll - 14",
  "ACT Direct Enroll - 32",
  "SAT Direct Enroll - 14",
  "SAT Direct Enroll - 32",
  "Standard E1",
  "Learning Center",
  "Learning Center 1 to 1",
  "Learning Center 4 to 1"
];

export const InquiryFormLeftPane: FC<InquiryFormLeftPaneProps> = ({
  onSelectInquirer,
  onSelectStudent,
  showContactFamilies,
  inquiryForm,
  onCreateNewContact,
  handleShowStudent,
  handleShowInquirer,
  isDetailsTab,
  detailsTabHeader,
  readOnly = false
}) => {
  const {
    watch,
    setValue,
    register,
    resetField,
    setError,
    clearErrors,
    getFieldState,
    control,
    formState: { errors }
  } = inquiryForm;
  const { activeCenterId } = useAccessControl();
  const { activeConstants, isLoading: centerConstantsLoading } = useCenterConstants(activeCenterId);
  const {
    activeServices,
    activePrograms,
    activeSubjects,
    activeTests,
    chainableFieldsDict: { servicesDict, subServicesDict } = { servicesDict: {}, subServicesDict: {} } as {
      servicesDict: ServiceDictionary;
      subServicesDict: SubServiceDictionary;
    },
    cecEnrollmentStatus
  } = activeConstants;
  const { activeRelationships, STUDENT_VALUE, isLoading: familyConstantsLoading } = useFamilyConstants();
  const inquiryConstantsQuery = useInquiryConstants();

  DISCOUNT_REASON_OTHER_ID = inquiryConstantsQuery.data?.DISCOUNT_REASON_OTHER_ID ?? "";
  DETAILS_TAB = !!isDetailsTab;

  const serviceIdWatch = watch("serviceId");
  const subServiceOptions = useMemo(() => {
    return (
      servicesDict?.[serviceIdWatch]?.subServices.filter(subService =>
        activePrograms.some(({ value }) => subService.value === value)
      ) ?? []
    );
  }, [serviceIdWatch, servicesDict, activePrograms]);

  const [feesDisabledStates, setFeesDisabledStates] = useState({
    // "aeFee.baseFee": true, // should always be disabled
    "aeFee.discountValue": readOnly,
    standardHourlyTuitionQuoted: readOnly,
    privateHourlyTuitionQuoted: readOnly,
    regFee: readOnly
  });
  const selectedSubServiceWatch = watch("selectedSubService");
  const subServiceId = selectedSubServiceWatch?.split("::")[0] ?? "";
  const [subjectOrTestOptions, subjectsLength, testsLength] = useMemo(() => {
    if (selectedSubServiceWatch) {
      const subjects =
        subServicesDict?.[subServiceId]?.subjects
          .filter(subject => activeSubjects.some(({ value }) => value === subject.value))
          .map(subject => ({ ...subject, value: `${subject.value}::subjectId` })) ?? [];
      const tests =
        subServicesDict?.[subServiceId]?.tests
          .filter(test => activeTests.some(({ value }) => value === test.value))
          .map(test => ({ ...test, value: `${test.value}::testId` })) ?? [];

      return [[...subjects, ...tests], subjects.length, tests.length];
    }

    return [[], 0, 0];
  }, [selectedSubServiceWatch]);
  const enableSubjectsOrTestsDropdown = Number(subjectsLength) || Number(testsLength);

  const centerAEFee = useMemo(
    () => activePrograms.find(p => p.value === subServiceId)?.aeFee || 0,
    [activePrograms, subServiceId]
  );

  const programId = watch("programId");

  useEffect(() => {
    if (subServiceId && !servicesDict?.[serviceIdWatch]?.subServices.find(s => s.value === subServiceId)) {
      setValue("selectedSubService", "");
    }

    if (subServiceId !== programId) {
      setValue("aeFee.baseFee", centerAEFee);
    }

    const selectedSubService = subServiceOptions.find(s => s.value === subServiceId);
    const subServiceName = selectedSubService?.name ?? "";
    const disabledFieldsStates = {
      // "aeFee.baseFee": true, // should always be disabled
      // (servicesThatDisableAEFee.includes(subServiceName) || readOnly) &&
      // !servicesThatEnableAEFee.includes(subServiceName),
      "aeFee.discountValue": servicesThatDisableAEFee.includes(subServiceName) || readOnly,
      standardHourlyTuitionQuoted:
        (servicesThatDisableStandardHourlyFee.includes(subServiceName) || readOnly) &&
        !servicesThatEnableStandardHourlyFee.includes(subServiceName),
      privateHourlyTuitionQuoted:
        (servicesThatDisablePrivHourlyFee.includes(subServiceName) || readOnly) &&
        !servicesThatEnablePrivHourlyFee.includes(subServiceName),
      regFee:
        (servicesThatDisableRegistrationFee.includes(subServiceName) || readOnly) &&
        !sericesThatEnableRegistrationFee.includes(subServiceName)
    };

    setFeesDisabledStates(disabledFieldsStates);

    // Reset fields that were disabled
    Object.entries(disabledFieldsStates)
      .filter(([_, disabled]) => disabled)
      .forEach(([field, _]) => resetField(field as keyof InquiryFormData, { keepError: false }));
  }, [serviceIdWatch, selectedSubServiceWatch]);

  const calculateDiscountedAeFee = () => {
    const { baseFee, discountValue, discountType } = watch("aeFee");
    const baseFeeNumber = typeof baseFee === "string" ? parseFloat(baseFee) : baseFee;

    let discountedFee = baseFeeNumber;
    switch (discountType) {
      case "$":
        discountedFee = baseFeeNumber - Number(discountValue);
        break;
      case "%":
        discountedFee = baseFeeNumber * (1 - Number(discountValue) / 100);
        break;
    }
    return discountedFee;
  };

  const discountType = watch("aeFee.discountType");

  useEffect(() => {
    const discountTypeId = inquiryConstantsQuery.data?.discountTypes
      ?.find(type => type.name === discountTypeCodes.get(discountType))
      ?.value?.toString();
    setValue("aeFee.discountTypeId", discountTypeId);
  }, [discountType]);

  const discountValue = Number(watch("aeFee.discountValue"));

  useEffect(() => {
    if (!discountValue) {
      setValue("aeFee.discountReasonId", "");
    }
  }, [discountValue]);

  const discountReasonId = watch("aeFee.discountReasonId");

  useEffect(() => {
    if (discountReasonId !== DISCOUNT_REASON_OTHER_ID) {
      setValue("aeFee.discountReasonOther", "");
    }
  }, [discountReasonId]);

  const relationshipId = watch("relationshipId");
  const isStudentInquirer = Boolean(relationshipId) && relationshipId === STUDENT_VALUE;

  const student = watch("student");
  const nameHero = useMemo(() => {
    if (!student || !student.displayName) {
      return "New Student";
    } else {
      return student.displayName;
    }
  }, [student]);

  const inquiryId = watch("id");
  const [activeKey, setActiveKey] = useState<string | null>(null);
  useEffect(() => {
    setActiveKey(null);
  }, [inquiryId]);

  const handleToggle = (key: string) => {
    setActiveKey(activeKey === key ? null : key);
  };

  const subHeaderClass = classNames({ "card-subheader mb-2": isDetailsTab });

  const sourceId = watch("sourceId");
  const showCecCheckbox = useMemo(() => {
    if (!sourceId) {
      return cecEnrollmentStatus;
    }

    const selection = inquiryConstantsQuery.data?.contactMethodSources?.find(cms => cms.value === sourceId);
    const isCecManagedOption = selection && selection.cecManagedOption;
    return cecEnrollmentStatus && isCecManagedOption;
  }, [cecEnrollmentStatus, sourceId, inquiryConstantsQuery.data?.contactMethodSources]);

  useEffect(() => {
    if (!showCecCheckbox) {
      setValue("cecManaged", false);
    }
  }, [showCecCheckbox, setValue]);

  const disableCecManaged = watch("takenBy")?.isDynamicsUser;

  // Determine if multiple selection is allowed based on sub-service configuration
  const allowsMultipleSubjects = subServiceOptions.some(
    ({ name, value }) => value === subServiceId && name === "Standard Subject Tutoring"
  );

  // Multiple Subjects selection requires Services and SubServices context,
  // but Zod schema definition is outside of component scope, so we need to do manual validation here.
  const multipleSubjectIdsWatch = watch("multipleSubjectIds");
  const selectedSubjectOrTestWatch = watch("selectedSubjectOrTest");

  useEffect(() => {
    if (subServiceId) {
      const state = getFieldState("multipleSubjectIds");
      if (allowsMultipleSubjects && (!multipleSubjectIdsWatch || !multipleSubjectIdsWatch.length) && state.isTouched) {
        setError(
          "multipleSubjectIds",
          {
            type: "required",
            message: "At least one subject must be selected"
          },
          { shouldFocus: false }
        );
      } else {
        clearErrors("multipleSubjectIds");
      }

      // Clear single selection when multiple is required
      if (allowsMultipleSubjects && selectedSubjectOrTestWatch) {
        setValue("selectedSubjectOrTest", "");
      }
    }
  }, [allowsMultipleSubjects, subServiceId, multipleSubjectIdsWatch, setError, clearErrors, setValue]);

  // Helper function to generate multi-select options for checkboxes
  const generateMultiSelectOptions = useMemo(() => {
    const options: MultiSelectOption[] = subjectOrTestOptions.map(sub => {
      return {
        label: sub.name,
        value: sub.value,
        isChecked: multipleSubjectIdsWatch?.includes(sub.value.split("::")[0]) ?? false
      };
    });
    return options;
  }, [allowsMultipleSubjects, multipleSubjectIdsWatch, subjectOrTestOptions]);

  // Helper function to generate option labels for multi-select
  const generateOptionLabels = useMemo(() => {
    const labels: Record<string, string> = {};
    subjectOrTestOptions.forEach(option => {
      labels[option.value] = option.name;
    });
    return labels;
  }, [subjectOrTestOptions, subjectsLength]);

  // Handle multi-select changes
  const handleMultiSelectChange = () => {
    const selectedValues = generateMultiSelectOptions.filter(o => o.isChecked).map(o => o.value.split("::")[0]);
    setValue("multipleSubjectIds", selectedValues, { shouldDirty: true, shouldTouch: true });
  };

  const getMultiSelectDisplayLabel = useMemo(() => {
    if (!multipleSubjectIdsWatch?.length) {
      return "Select Subject / Test";
    }

    const selectedNames = multipleSubjectIdsWatch
      ?.map(value => subjectOrTestOptions.find(option => option.value === `${value}::subjectId`)?.name)
      .filter(Boolean);

    if (!selectedNames!.length) {
      return "Select Subject / Test";
    }

    return selectedNames.join(", ");
  }, [multipleSubjectIdsWatch, generateOptionLabels]);

  const [
    regFeeWatch,
    aeFeeDiscountValueWatch,
    standardHourlyTuitionQuotedWatch,
    privateHourlyTuitionQuotedWatch,
    callbackSpecialQuotedWatch
  ] = watch([
    "regFee",
    "aeFee.discountValue",
    "standardHourlyTuitionQuoted",
    "privateHourlyTuitionQuoted",
    "callbackSpecialQuoted"
  ]); // to trigger re-render when sub-service changes

  return (
    <CardBody className="space-rows">
      {!isDetailsTab && (
        <>
          <h2 className="mb-4">{nameHero}</h2>
          <Row>
            <Col md={6}>
              <ContactSearchController
                label="Initial Inquirer"
                onSelect={onSelectInquirer}
                addNewLabel="Create New Contact"
                onCreateNewClick={() => onCreateNewContact("inquirer")}
                name="inquirer"
                control={control}
                isRequired
              />
              <button
                className="mt-2 btn p-0 m-n text-left btn-link"
                onClick={handleShowInquirer}
                disabled={!watch("inquirer")?.id}
              >
                Initial Inquirer Details
              </button>
            </Col>
            <Col md={6}>
              <SelectField
                isRequired
                label="Relationship to Student"
                validationState={errors.relationshipId ? "error" : undefined}
                isLoading={familyConstantsLoading}
                {...register("relationshipId")}
              >
                <option value="">Select Relationship</option>
                {activeRelationships
                  .sort(({ name: left }, { name: right }) => left.localeCompare(right))
                  .map(({ name, value }) => (
                    <option key={value} value={value}>
                      {name}
                    </option>
                  ))}
              </SelectField>
            </Col>
            <Col md={6}>
              <ContactSearchController
                label="Student"
                onSelect={onSelectStudent}
                addNewLabel="Create New Contact"
                onCreateNewClick={() => onCreateNewContact("student")}
                name="student"
                control={control}
                isRequired
                disabled={isStudentInquirer}
              />
              <button
                className="mt-2 btn p-0 m-n text-left btn-link"
                onClick={handleShowStudent}
                disabled={!watch("student")?.id}
              >
                Student Details
              </button>
            </Col>
            <Col md={6}>
              <div className="form-floating">
                <input
                  type="text"
                  id="family"
                  value={watch("family")?.name || ""}
                  className={classNames("form-control", {
                    "is-invalid": errors["family"]
                  })}
                  placeholder="Selected Family"
                  disabled
                  readOnly
                />
                <label htmlFor="family">
                  Selected Family{" "}
                  <span className="required" aria-hidden="true">
                    *
                  </span>
                </label>
                <button
                  className="mt-2 btn p-0 m-n text-left btn-link"
                  onClick={showContactFamilies}
                  disabled={!watch("inquirer")?.id || !watch("student")?.id}
                >
                  Change Family
                </button>
              </div>
            </Col>
          </Row>
        </>
      )}

      {detailsTabHeader}
      {isDetailsTab && (
        <Row>
          <Col>
            <Accordion activeKey={activeKey}>
              <AccordionItem eventKey="0" onClick={() => handleToggle("0")}>
                <AccordionHeader as="h3">Initial Contact</AccordionHeader>
                <AccordionBody>
                  <InitialContact isDetailsTab inquiryForm={inquiryForm} />
                </AccordionBody>
              </AccordionItem>
            </Accordion>
          </Col>
        </Row>
      )}

      {isDetailsTab && showCecCheckbox && (
        <Row>
          <Col>
            <div className="form-check mt-4">
              <label className="form-check-label" htmlFor="CEC">
                CEC Managed Inquiry
              </label>
              <input
                className="form-check-input"
                type="checkbox"
                id="CEC"
                disabled={disableCecManaged}
                {...register("cecManaged")}
              />
            </div>
          </Col>
        </Row>
      )}

      <Row>
        {isDetailsTab && (
          <Col md={12}>
            <h3 className={subHeaderClass}>Inquiry Detail</h3>
          </Col>
        )}
        <Col md={4}>
          <SelectField
            disableColClass
            label="Service"
            validationState={errors["serviceId"] ? "error" : undefined}
            isRequired
            disabled={readOnly}
            isLoading={centerConstantsLoading}
            {...register("serviceId")}
          >
            <option value="">Select Service</option>
            {activeServices?.map(({ value, name }) => (
              <option key={value} value={value}>
                {name}
              </option>
            ))}
          </SelectField>
        </Col>
        <Col md={4}>
          <SelectField
            disableColClass
            label="Sub-Service"
            validationState={errors["selectedSubService"] ? "error" : undefined}
            isRequired
            disabled={readOnly}
            isLoading={centerConstantsLoading}
            {...register("selectedSubService")}
          >
            <option value="">Select Sub-Service</option>
            {subServiceOptions?.map(({ value, name }) => {
              const subjectsLength = subServicesDict?.[value]?.subjects?.length ?? 0;
              const testsLength = subServicesDict?.[value]?.tests?.length ?? 0;
              return (
                <option key={value} value={`${value}::${subjectsLength + testsLength}`}>
                  {name}
                </option>
              );
            })}
          </SelectField>
        </Col>
        <Col md={4}>
          {(allowsMultipleSubjects && (
            <MultiSelectField
              disableColClass
              label="Subject / Test"
              selectedDisplayText={getMultiSelectDisplayLabel}
              validationState={errors["multipleSubjectIds"] ? "error" : undefined}
              disabled={readOnly || !enableSubjectsOrTestsDropdown}
              isRequired={!!enableSubjectsOrTestsDropdown}
              options={generateMultiSelectOptions}
              optionLabels={generateOptionLabels}
              onChange={handleMultiSelectChange}
            />
          )) || (
            <SelectField
              disableColClass
              label="Subject / Test"
              validationState={errors["subjectId"] ? "error" : undefined}
              disabled={readOnly || !enableSubjectsOrTestsDropdown}
              isRequired={!!enableSubjectsOrTestsDropdown}
              isLoading={centerConstantsLoading}
              {...register("selectedSubjectOrTest")}
            >
              <option value="">Select Subject / Test</option>
              {subjectOrTestOptions.map(({ value, name }, index) => (
                <option key={value} value={value}>
                  {name}
                </option>
              ))}
            </SelectField>
          )}
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <h3 className={subHeaderClass}>Notes</h3>
        </Col>
        <Col md={12}>
          <div className="form-floating flex-grow-1">
            <textarea
              className="form-control"
              id="inquiry-form-notes"
              style={{ height: "100%", minHeight: "200px", overflow: "hidden", overflowY: "auto" }}
              disabled={readOnly}
              {...register("notes")}
            />
            <label htmlFor="inquiry-form-notes">Notes</label>
          </div>
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <h3 className={subHeaderClass}>Objections</h3>
        </Col>
        <Col md={12}>
          <CheckboxFieldset disabled={readOnly}>
            {inquiryConstantsQuery.data?.activeObjections.map(({ name, value }) => (
              <CheckboxField key={value} label={name} value={value} {...register("objections")} />
            ))}
          </CheckboxFieldset>
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <h3 className={subHeaderClass}>AE Fee</h3>
        </Col>
        <Col md={6}>
          <InputField
            disableColClass
            type="number"
            label="Center AE Fee"
            step=".01"
            labelClassName="single-line-label mx-5 px-sm-2 bg-transparent"
            addOnClassName="fas icon fa-dollar-sign"
            validationState={errors.aeFee?.baseFee ? "error" : undefined}
            // disabled={feesDisabledStates["aeFee.baseFee"]}
            disabled={true}
            {...register("aeFee.baseFee")}
          />
        </Col>
        <Col md={6}>
          <div className="d-flex">
            <div className="me-3">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="radio"
                  value="$"
                  id=":r90:"
                  disabled={readOnly}
                  {...register("aeFee.discountType")}
                />
                <label className="form-check-label" htmlFor=":r90:">
                  $
                </label>
              </div>
              <div className="form-check flex-grow-1">
                <input
                  className="form-check-input"
                  type="radio"
                  value="%"
                  id=":r91:"
                  disabled={readOnly}
                  {...register("aeFee.discountType")}
                />
                <label className="form-check-label" htmlFor=":r91:">
                  %
                </label>
              </div>
            </div>
            <div className="w-100">
              <InputField
                disableColClass
                type="number"
                label="Discount"
                step=".01"
                validationState={errors.aeFee?.discountValue ? "error" : undefined}
                disabled={feesDisabledStates["aeFee.discountValue"]}
                labelClassName="single-line-label mx-5 px-sm-2 bg-transparent"
                addOnClassName={discountType === "$" ? "fas icon fa-dollar-sign" : "fas icon fa-percent"}
                {...register("aeFee.discountValue")}
                onBlur={_ => setValue("aeFee.discountValue", formatToFloat(aeFeeDiscountValueWatch))}
              />
            </div>
          </div>
        </Col>
        <Col md={6}>
          <InputField
            disableColClass
            type="number"
            label="Quoted AE Fee"
            value={(calculateDiscountedAeFee() ?? 0).toFixed(2)}
            disabled
            labelClassName="single-line-label mx-5 px-sm-2 bg-transparent"
            addOnClassName="fas icon fa-dollar-sign"
          />
        </Col>

        <Col md={6}>
          <SelectField
            label="Discount Reason"
            validationState={errors.aeFee?.discountReasonId ? "error" : undefined}
            disabled={readOnly || !discountValue || feesDisabledStates["aeFee.discountValue"]}
            isLoading={inquiryConstantsQuery.isLoading}
            isRequired={!!discountValue}
            {...register("aeFee.discountReasonId")}
          >
            <option value="">Select Reason</option>
            {inquiryConstantsQuery.data?.activeDiscountReasons.map(({ name, value }) => (
              <option key={value} value={value}>
                {name}
              </option>
            )) ?? []}
          </SelectField>
        </Col>
        {!discountReasonId ||
          (discountReasonId === DISCOUNT_REASON_OTHER_ID && (
            <Col md={12}>
              <InputField
                type="text"
                label="Other Discount Reason Details"
                validationState={errors.aeFee?.discountReasonOther ? "error" : undefined}
                {...register("aeFee.discountReasonOther")}
                isRequired
              />
            </Col>
          ))}
      </Row>
      <Row>
        <Col md={12}>
          <h3 className={subHeaderClass}>Hourly Tuition {isDetailsTab ? "QUOTED" : ""}</h3>
        </Col>
        <Col md={6}>
          <InputField
            disableColClass
            type="number"
            label="Standard Hourly"
            step=".01"
            validationState={errors.standardHourlyTuitionQuoted ? "error" : undefined}
            labelClassName="single-line-label mx-5 px-sm-2 bg-transparent"
            addOnClassName="fas icon fa-dollar-sign"
            disabled={feesDisabledStates.standardHourlyTuitionQuoted}
            {...register("standardHourlyTuitionQuoted")}
            onBlur={_ => setValue("standardHourlyTuitionQuoted", formatToFloat(standardHourlyTuitionQuotedWatch))}
          />
        </Col>
        <Col md={6}>
          <InputField
            disableColClass
            type="number"
            label="1-1 Hourly"
            step=".01"
            validationState={errors.privateHourlyTuitionQuoted ? "error" : undefined}
            labelClassName="single-line-label mx-5 px-sm-2 bg-transparent"
            addOnClassName="fas icon fa-dollar-sign"
            disabled={feesDisabledStates.privateHourlyTuitionQuoted}
            {...register("privateHourlyTuitionQuoted")}
            onBlur={_ => setValue("privateHourlyTuitionQuoted", formatToFloat(privateHourlyTuitionQuotedWatch))}
          />
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <h3 className={`${subHeaderClass} mb-4`}>Callback Special Quoted</h3>
          <InputField
            disableColClass
            type="number"
            label="Callback Special"
            step=".01"
            validationState={errors.callbackSpecialQuoted ? "error" : undefined}
            labelClassName="single-line-label mx-5 px-sm-2 bg-transparent"
            addOnClassName="fas icon fa-dollar-sign"
            disabled={readOnly}
            {...register("callbackSpecialQuoted")}
            onBlur={_ => setValue("callbackSpecialQuoted", formatToFloat(callbackSpecialQuotedWatch))}
          />
        </Col>
        <Col md={6}>
          <h3 className={`${subHeaderClass} mb-4`}>Registration Fee</h3>
          <InputField
            disableColClass
            type="number"
            label="Center Reg Fee"
            step=".01"
            validationState={errors.regFee ? "error" : undefined}
            labelClassName="single-line-label mx-5 px-sm-2 bg-transparent"
            addOnClassName="fas icon fa-dollar-sign"
            disabled={feesDisabledStates.regFee}
            {...register("regFee")}
            onBlur={_ => setValue("regFee", formatToFloat(regFeeWatch))}
          />
        </Col>
      </Row>
    </CardBody>
  );
};

const formatToFloat = (aeFee?: string | number, fractionDigits?: number): string | number =>
  (parseFloat(aeFee?.toString() || "") || 0).toFixed(fractionDigits || 2);
